import type {VoidFunctionComponent} from "react";
import React from "react";
import s from "./ContainerDocumentation.module.scss";
import {Example} from "../Example";
import {ComponentDescription} from "../documentationTools/ComponentDescription";
import {Property} from "../documentationTools/Property";
import {PropertyListHeader} from "../documentationTools/PropertyListHeader";
import {IPropertyWrapper} from "../documentationTools/DocumentationTypes";
import {sortProperties} from "../documentationTools/documentation-utility";
import Container from "../../../components/common/tools/Container/Container";
import {getLoremIpsumText} from "../../../util/string-util";

export interface IContainerDocumentationProps {
}

export const ContainerDocumentation: VoidFunctionComponent<IContainerDocumentationProps> = ({}) => {
  let propertyIndex = 0;

  const properties: IPropertyWrapper[] = [
    {
      isRequired: false,
      name: "label",
      element: <Property
        key={++propertyIndex}
        name={"label"}
        description={"label that appears at the top of the container."}
        isRequired={false}
        type={"string"}
        examples={<>
          <Example title={"label = 'My container'"}>
            <Container
              label={"My container"}
            >
              {getLoremIpsumText(50)}
            </Container>
          </Example>
        </>}
      />
    },
    {
      isRequired: false,
      name: "textAlign",
      element: <Property
        key={++propertyIndex}
        name={"textAlign"}
        description={'Determines the alignment of Container content.'}
        isRequired={false}
        type={'"left" | "center" | "right"'}
        defaultValue={"center"}
        examples={<>
          <Example title={"label = 'My container'"}>
            <div style={{width: "700px"}}>
              <Container
                textAlign={"right"}
              >
                {getLoremIpsumText(300)}
              </Container>
            </div>
          </Example>
        </>}
      />
    },
    {
      isRequired: false,
      name: "isCollapsible",
      element: <Property
        key={++propertyIndex}
        name={"isCollapsible"}
        description={<>
          Determines if the Container content can be collapsed. If collapsible, a clickable chevron will appear to the left of the Container label.
          <br />
          <span className={s.warning}> NOTE: this property currently does not work as it should. Containers with isCollapsible = false can still be collapsed by clicking on their labels</span>
        </>}
        isRequired={false}
        type={"boolean"}
        defaultValue={"false"}
        examples={<>
          <Example title={"isCollapsible = true"}>
            <div style={{width: "700px"}}>
              <Container
                isCollapsible={true}
              >
                {getLoremIpsumText(300)}
              </Container>
            </div>
          </Example>
        </>}
      />
    },
    {
      isRequired: false,
      name: "isInitiallyCollapsed",
      element: <Property
        key={++propertyIndex}
        name={"isInitiallyCollapsed"}
        description={'Only applies if the Container is collapsible. Determines the initial state of the Container (expanded or collapsed).'}
        isRequired={false}
        type={"boolean"}
        defaultValue={"false"}
        examples={<>
          <Example title={"isInitiallyCollapsed = true"}>
            <div style={{width: "700px"}}>
              <Container
                isCollapsible={true}
                isInitiallyCollapsed={true}
              >
                {getLoremIpsumText(300)}
              </Container>
            </div>
          </Example>
        </>}
      />
    },
    {
      isRequired: true,
      name: "children",
      element: <Property
        key={++propertyIndex}
        name={"children"}
        description={'The contents of the Container.'}
        isRequired={true}
        type={"ReactNode"}
        examples={<>
          <Example title={"Container with children"}>
            <div style={{width: "700px"}}>
              <Container>
                {getLoremIpsumText(300)}
              </Container>
            </div>
          </Example>
        </>}
      />
    },
    {
      isRequired: false,
      name: "additionalClassName",
      element: <Property
        key={++propertyIndex}
        name={"additionalClassName"}
        description={'Additional css classes that can be applied to the outer dom element of the Container tool.'}
        isRequired={false}
        type={"string"}
        examples={<>
          <Example title={'Container with additional class name "cheese"'}>
            <div style={{width: "700px"}}>
              <Container
                additionalClassName={"cheese"}
              >
                {getLoremIpsumText(300)}
              </Container>
            </div>
          </Example>
        </>}
      />
    },


  ];

  sortProperties(properties);

  return <div className={s.container}>
    <ComponentDescription
      name={"Container"}
      description={"Visually groups content together."}
    />

    <PropertyListHeader properties={properties} />

    {properties.map((property, index) => {
      return <React.Fragment key={index}>
        {property.element}
        {index < properties.length - 1 && <hr className={s.propertyDivider} />}
      </React.Fragment>
    })}
  </div>;
}
